import { createContext, useContext, useEffect, useState, type PropsWithChildren } from 'react';
import type { RxCollection, RxDocument, RxQuery } from 'rxdb';

import type { RxDocumintDatabase } from '@flintmint/core-shared/rxdb';

const RxDbContext = createContext<RxDocumintDatabase | undefined>(undefined);

export const RxDbProvider = ({ db, children }: PropsWithChildren<{ db?: RxDocumintDatabase }>) => (
  <RxDbContext.Provider value={db}>{children}</RxDbContext.Provider>
);

export const useRxDb = (): RxDocumintDatabase | undefined => useContext(RxDbContext);

export const useRxCollection = <T,>(name: string): RxCollection<T> | null => {
  const db = useRxDb();
  const collections = db?.collections as Record<string, RxCollection<T>> | undefined;
  return collections?.[name] ?? null;
};

type QueryConstructor<T> = (collection: RxCollection<T>) => RxQuery<T, RxDocument<T> | RxDocument<T>[] | null> | undefined;

export interface RxDataResult<T> {
  result: RxDocument<T>[];
  isFetching: boolean;
}

export const useRxData = <T,>(name: string, queryConstructor: QueryConstructor<T>): RxDataResult<T> => {
  const collection = useRxCollection<T>(name);
  const query = collection ? queryConstructor(collection) : undefined;

  const [state, setState] = useState<RxDataResult<T>>({ result: [], isFetching: true });
  const [trackedQuery, setTrackedQuery] = useState(query);

  // Derived-state-during-render reset: when the query identity changes on a mounted hook
  // (e.g. DocumentWorkspace switching note ids in place), no commit may expose the previous
  // query's result under the new query as settled — consumers like useRxDocument would read
  // "isFetching false, doc id mismatch" as a real 404. We keep the previous result visible so
  // reactive list swaps don't flicker to empty, but flag isFetching until the new subscription
  // emits, so nothing reads the stale result as settled. Query identity is stable per logical
  // query via RxDB's QueryCache, which the effect's [query, collection] deps already rely on.
  if (query !== trackedQuery) {
    setTrackedQuery(query);
    setState((prev) => (query ? { result: prev.result, isFetching: true } : { result: [], isFetching: !collection }));
  }

  useEffect(() => {
    if (!query) {
      setState({ result: [], isFetching: !collection });
      return;
    }

    setState((prev) => ({ ...prev, isFetching: true }));

    const subscription = query.$.subscribe((docs) => {
      const result = Array.isArray(docs) ? docs : docs ? [docs] : [];
      setState({ result: result as RxDocument<T>[], isFetching: false });
    });

    return () => subscription.unsubscribe();
  }, [query, collection]);

  return state;
};
